import CheckIconAlt from '@src/svgs/CheckIconAlt';
import { useEffect } from 'react';
import styled from 'styled-components';

import { useVideoTemplatesContext } from '../sidepanel/VideoTemplateSettings/VideoTemplatesContext';
import { VideoTemplatesGrid } from '../sidepanel/VideoTemplateSettings/VideoTemplatesGrid';

import type { Dispatch, SetStateAction } from 'react';

interface VideoTemplateFilterComponentProps {
  align?: 'left' | 'center';
  /**
   * Row label. The default is the agreed wording for every surface — it names the
   * thing rather than the action, matching its sibling rows ("Target Goal",
   * "Target Audiences"). Overridable, but no caller needs to today.
   */
  title?: string;
  selectedVideoTemplateId: string | undefined;
  setSelectedVideoTemplateId: (id: string | undefined) => void;
  useVideoTemplateFilter: boolean;
  setUseVideoTemplateFilter: Dispatch<SetStateAction<boolean>>;
}

export function VideoTemplateFilterComponent({
  align = 'center',
  title = 'Video Templates',
  selectedVideoTemplateId,
  setSelectedVideoTemplateId,
  useVideoTemplateFilter,
  setUseVideoTemplateFilter,
}: VideoTemplateFilterComponentProps) {
  const { videoTemplates } = useVideoTemplatesContext();

  useEffect(() => {
    if (videoTemplates.length > 0 && !selectedVideoTemplateId) {
      setSelectedVideoTemplateId(videoTemplates[0].id);
    }
  }, [videoTemplates, selectedVideoTemplateId]);

  return (
    <VideoTemplateFilterSection data-testid="clip-producer-template-toggle">
      <CheckBoxContainer
        align={align}
        onClick={() => setUseVideoTemplateFilter(prev => !prev)}
        disabled={videoTemplates.length === 0}
      >
        <CheckBox isChecked={useVideoTemplateFilter} role="button">
          {useVideoTemplateFilter && <CheckIconAlt />}
        </CheckBox>
        <div className="checkbox-text">{title}</div>
      </CheckBoxContainer>

      {useVideoTemplateFilter && (
        <>
          {videoTemplates.length === 0 ? (
            <NoTemplatesText>No video templates found</NoTemplatesText>
          ) : (
            <>
              <HelperText align={align}>
                Select a template to apply branding to your clips
              </HelperText>
              <TemplateGridContainer>
                <VideoTemplatesGrid
                  onAddVideoTemplate={template =>
                    setSelectedVideoTemplateId(template.id)
                  }
                  activeTemplateId={selectedVideoTemplateId}
                  videoTemplates={videoTemplates}
                  actionLabel="Select"
                />
              </TemplateGridContainer>
            </>
          )}
        </>
      )}
    </VideoTemplateFilterSection>
  );
}

const VideoTemplateFilterSection = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 8px;
`;

const CheckBoxContainer = styled.div<{
  align: 'left' | 'center';
  disabled?: boolean;
}>`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: ${props =>
    props.align === 'center' ? 'center' : 'flex-start'};
  gap: 8px;
  cursor: ${props => (props.disabled ? 'not-allowed' : 'pointer')};
  opacity: ${props => (props.disabled ? 0.5 : 1)};
  user-select: none;

  .checkbox-text {
    color: var(--foreground);
    font-size: var(--text-xs);
    font-weight: var(--font-weight-medium);
    line-height: 150%;
  }
`;

const CheckBox = styled.div<{ isChecked: boolean }>`
  width: 14px;
  height: 14px;
  min-width: 14px;
  min-height: 14px;
  border-radius: 2px;
  border: 1px solid
    ${props => (props.isChecked ? 'var(--accent)' : 'var(--text-muted)')};
  background: ${props => (props.isChecked ? 'var(--accent)' : 'transparent')};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all var(--transition-fast) ease;

  svg {
    width: 10px;
    height: 10px;
    color: var(--background);
  }
`;

const NoTemplatesText = styled.div`
  color: var(--text-muted);
  font-size: var(--text-xs);
  text-align: center;
  padding: 8px;
`;

const TemplateGridContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`;

const HelperText = styled.div<{ align: 'left' | 'center' }>`
  color: var(--text-muted);
  font-size: var(--text-xs);
  line-height: 140%;
  text-align: ${props => props.align};
`;
